import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  generateSummaryReport, 
  formatReportAsText, 
  formatReportAsHTML 
} from '../../utils/reportGenerator';
import { downloadFile } from '../../utils/exportService';
import { useToast } from '../ui/Toast';
import './ReportExport.css';

/**
 * Report Export Component
 * Provides easy export and sharing options for dashboard reports
 */
const ReportExport = ({ 
  userData, 
  splitData, 
  txData, 
  walletData, 
  badgeData, 
  phoneAuthData, 
  financialFlow,
  period 
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const { showToast } = useToast();

  const handleExport = async (format) => {
    setIsGenerating(true);
    try {
      const report = generateSummaryReport({
        userData,
        splitData,
        txData,
        walletData,
        badgeData,
        phoneAuthData,
        financialFlow
      }, period);

      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `wesplit-report-${period.toLowerCase().replace(/\s+/g, '-')}-${timestamp}`;

      switch (format) {
        case 'text':
          const textReport = formatReportAsText(report);
          downloadFile(textReport, `${filename}.txt`, 'text/plain');
          showToast('Text report downloaded', 'success');
          break;
        
        case 'html':
          const htmlReport = formatReportAsHTML(report);
          downloadFile(htmlReport, `${filename}.html`, 'text/html');
          showToast('HTML report downloaded', 'success');
          break;
        
        case 'json':
          downloadFile(JSON.stringify(report, null, 2), `${filename}.json`, 'application/json');
          showToast('JSON report downloaded', 'success');
          break;
        
        case 'copy':
          const textForClipboard = formatReportAsText(report);
          await navigator.clipboard.writeText(textForClipboard);
          showToast('Report copied to clipboard!', 'success');
          break;
        
        default:
          showToast('Unknown export format', 'error');
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      showToast('Failed to export report', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleShare = async () => {
    try {
      const report = generateSummaryReport({
        userData,
        splitData,
        txData,
        walletData,
        badgeData,
        phoneAuthData,
        financialFlow
      }, period);

      const textReport = formatReportAsText(report);
      
      if (navigator.share) {
        await navigator.share({
          title: `WeSplit Analytics Report - ${period}`,
          text: textReport,
        });
        showToast('Report shared successfully!', 'success');
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(textReport);
        showToast('Report copied to clipboard!', 'success');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error sharing report:', error);
        showToast('Failed to share report', 'error');
      }
    }
  };

  return (
    <div className="report-export-container">
      <div className="report-export-header">
        <h3>
          <i className="bi bi-file-earmark-text icon-brand"></i>
          Export & Share Report
        </h3>
        <p className="report-export-description">
          Generate a comprehensive, easy-to-read report for your team
        </p>
      </div>

      <div className="report-export-actions">
        <button
          className="report-export-btn primary"
          onClick={() => handleExport('text')}
          disabled={isGenerating}
          title="Download as plain text file"
        >
          <i className="bi bi-file-text"></i>
          Download Text Report
        </button>

        <button
          className="report-export-btn"
          onClick={() => handleExport('html')}
          disabled={isGenerating}
          title="Download as HTML file (for email)"
        >
          <i className="bi bi-file-earmark-code"></i>
          Download HTML
        </button>

        <button
          className="report-export-btn"
          onClick={() => handleExport('json')}
          disabled={isGenerating}
          title="Download as JSON (for developers)"
        >
          <i className="bi bi-filetype-json"></i>
          Download JSON
        </button>

        <button
          className="report-export-btn"
          onClick={() => handleExport('copy')}
          disabled={isGenerating}
          title="Copy report to clipboard"
        >
          <i className="bi bi-clipboard"></i>
          Copy to Clipboard
        </button>

        <button
          className="report-export-btn share"
          onClick={handleShare}
          disabled={isGenerating}
          title="Share report (native share dialog)"
        >
          <i className="bi bi-share"></i>
          Share Report
        </button>
      </div>

      {isGenerating && (
        <div className="report-export-loading">
          <i className="bi bi-arrow-repeat spin"></i>
          Generating report...
        </div>
      )}

      <div className="report-export-info">
        <p>
          <i className="bi bi-info-circle"></i>
          Reports include: User metrics, Financial overview, Activity stats, and Key insights
        </p>
      </div>
    </div>
  );
};

ReportExport.propTypes = {
  userData: PropTypes.object,
  splitData: PropTypes.object,
  txData: PropTypes.object,
  walletData: PropTypes.object,
  badgeData: PropTypes.object,
  phoneAuthData: PropTypes.object,
  financialFlow: PropTypes.object,
  period: PropTypes.string.isRequired
};

export default ReportExport;

